<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { useToast } from '../../composables/useToast';
import AssetsRepository from '@repositories/assets.repository';
import TypeAssetsRepository from '@repositories/typeAssets.repository';
import CompaniesRepository from '@repositories/companies.repository';
import { IAsset } from '../../types/assets.type';
import { ITypeAsset } from '../../types/typeAssets.type';
import { ICompany } from '../../types/company.type';
import { useI18n } from "vue-i18n";
import DataTable from "@components/DataTable.vue";

const isLoadingAssets = ref<boolean>(false);

const store = Store();
const { t } = useI18n();
const { $toast } = useToast();
const { assets: assetsPinia }: any = storeToRefs(store);
const assets = ref<IAsset[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedAsset = ref<IAsset | null>(null);
const isViewActive = ref<boolean>(true);
const typeAssetsData = ref<ITypeAsset[]>([]);
const companies = ref<ICompany[]>([]);
const appliedFilters = ref<Record<string, any>>({});
const fieldsTableInactive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'description',
    label: 'Asset',
  },
  {
    key: 'restore',
    label: 'Restaurar',
  },
]);
const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'description',
    label: 'Assets',
  },
  {
    key: 'riskType',
    label: 'Riesgo',
    formatter: (e: any) => e?.description || null,
  },
  {
    key: 'company',
    label: 'Sponsor',
    formatter: (e: any) => e?.name ?? 'TODAS',
  },
  {
    key: 'typeAsset',
    label: 'Tipo de Asset',
    formatter: (e: any) => e?.name ?? '-',
  },
  {
    key: 'indemnificationLimitMin',
    label: t('global.limite_minimo'),
    formatter: (e: any) => e ?? '-',
  },
  {
    key: 'indemnificationLimitMax',
    label: t('global.limite_maximo'),
    formatter: (e: any) => e ?? '-',
  },
  {
    key: 'assetInfo',
    label: 'Editar',
  },
  {
    key: 'delete',
    label: 'Eliminar',
  },
]);
const fieldsFilter = computed<any>(() => [
  {
    key: 'search',
    title: 'Buscar',
    type: 'text',
    placeholder: 'Buscar por descripcion del asset',
    value: null,
    cols: 3,
  },
  {
    key: 'companyName',
    title: 'Nombre del sponsor',
    type: 'select',
    label: 'name',
    placeholder: 'Buscar por sponsor',
    value: null,
    options: companies.value,
    formatValue: (val: any) => val?.name ?? null,
    cols: 3,
  },
  {
    key: 'riskType',
    title: 'Tipo de riesgo',
    label: 'description',
    type: 'select',
    placeholder: 'Buscar por tipo de riesgo',
    value: null,
    formatValue: (val: any) => val?.description ?? null,
    options: [
      { id: 1, description: 'LOW' },
      { id: 2, description: 'MEDIUM' },
      { id: 3, description: 'HIGH' },
    ],
    cols: 3,
  },
]);
const searchFormRef = ref<any>(null);

const getAssetsFromAPI = async (
  page: number,
  deleted: boolean = false,
  params = {},
) => {
  isLoadingAssets.value = true;
  try {
    const { data } = await AssetsRepository.getAssets({
      page,
      deleted,
      ...params,
    });
    if (data && Array.isArray(data?.assets)) {
      paginationData.value = data.pagination;
      updateAssets(data.assets);
      return data.assets;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching assets:', error);
    return [];
  } finally {
    isLoadingAssets.value = false;
  }
};

const getTypeAssetsFromAPI = async () => {
  try {
    const data = await TypeAssetsRepository.getTypeAssets();
    typeAssetsData.value = data?.data || [];
  } catch (error) {
    console.error('Error while fetching typeAssets:', error);
    return [];
  }
};

const getCompaniesFromApi = async () => {
  try {
    const data = await CompaniesRepository.getCompanies(true);
    if (Array.isArray(data?.companies)) {
      companies.value = data.companies;
    }
  } catch (error) {
    console.error('Error while fetching companies:', error);
  }
};

const updateAssets = (newAssets: IAsset[]) => {
  assets.value = newAssets;
  store.setProp({ key: 'assets', value: newAssets });
};

const loadAssets = async () => {
  await getAssetsFromAPI(1);
  getTypeAssetsFromAPI();
  getCompaniesFromApi();
};

onMounted(loadAssets);

// Create inputs from modals
const fieldsInputAsset = computed(() => {
  return [
    {
      key: 'description',
      title: 'Nombre del Asset',
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: 'Nuevo asset',
      value: selectedAsset.value?.description,
      cols: 12,
    },
    {
      key: 'companyId',
      label: 'name',
      selectLabel: null,
      value: selectedAsset.value?.company?.id,
      searchable: true,
      title: 'Sponsor',
      type: 'select',
      placeholder: 'Nombre del sponsor',
      formatValue: (val: any) => val.id,
      cols: 12,
      options: companies.value,
    },
    {
      key: 'riskTypeId',
      label: 'description',
      value: selectedAsset.value?.riskType.id,
      searchable: true,
      title: 'Riesgo',
      type: 'select',
      trackBy: 'id',
      placeholder: 'Tipo de riesgo',
      formatValue: (val: any) => val.id,
      cols: 12,
      options: [
        { id: 1, description: 'LOW' },
        { id: 2, description: 'MEDIUM' },
        { id: 3, description: 'HIGH' },
      ],
    },
    {
      key: 'indemnificationLimitMin',
      value: selectedAsset.value?.indemnificationLimitMin,
      title: t('global.limite_minimo'),
      type: 'text',
      placeholder: t('global.limite_minimo'),
      cols: 12,
      formatValue: (val: any) => val === '' ? null : val,
    },
    {
      key: 'indemnificationLimitMax',
      value: selectedAsset.value?.indemnificationLimitMax,
      title: t('global.limite_maximo'),
      type: 'text',
      placeholder: t('global.limite_maximo'),
      cols: 12,
      formatValue: (val: any) => val === '' ? null : val,
    },
    {
      key: 'typeAssetId',
      label: 'name',
      value: selectedAsset.value?.typeAsset?.id,
      searchable: true,
      title: 'Tipo de Asset',
      type: 'select',
      trackBy: 'id',
      placeholder: 'Tipo de asset',
      formatValue: (val: any) => val?.id ?? null,
      cols: 12,
      options: typeAssetsData.value,
    },
  ];
});

// UI
const onChangePage = async (page: number) => {
  getAssetsFromAPI(page, !isViewActive.value, appliedFilters.value);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = 'Agregar asset';
  isModalOpen.value = true;
  modalType.value = 'modalCreateAsset';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedAsset.value = null;
};

const handleActive = async () => {
  const assets = await getAssetsFromAPI(1, false, appliedFilters.value);
  updateAssets(assets);
  currentPage.value = 1;
  isViewActive.value = true;
};

const handleInactive = async () => {
  const assetsDeleted = await getAssetsFromAPI(1, true, appliedFilters.value);
  updateAssets(assetsDeleted);
  currentPage.value = 1;
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: IAsset) => {
  modalTitle.value = isViewActive.value
    ? `Borrar el Asset con id: ${item.id}`
    : `Restaurar el Asset con id: ${item.id}`;
  modalType.value = 'modalDialog';
  selectedAsset.value = item;
  isModalOpen.value = true;
};

// Filter

const handleOnSubmit = async ({ items }: any) => {
  appliedFilters.value = items;
  currentPage.value = 1;
  getAssetsFromAPI(1, !isViewActive.value, items);
};

const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  appliedFilters.value = {};
  currentPage.value = 1;
  getAssetsFromAPI(1, !isViewActive.value);
}

//CRUD
const handleCreateAsset = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  try {
    const data = await AssetsRepository.createAsset(items);
    store.setProp({ key: 'assets', value: [...assets.value, data] });
	assets.value = [...assets.value, data];
    $toast?.success(`Asset agregado con exito`);
  } catch (error) {
    $toast?.error('Ocurrió un error al intentar crear');
  }

  handleClose();
};

const handleSelectAsset = (assetData: IAsset) => {
  modalTitle.value = 'Actualizar Asset';
  (isModalOpen.value = true), (modalType.value = 'modalUpdateAsset');
  selectedAsset.value = assetData;
};

const handleUpdateAsset = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedAsset.value) return;
  const assetForUpdate = {
    ...items,
    id: selectedAsset.value.id,
  };

  try {
    const data = await AssetsRepository.updateAsset(assetForUpdate);
    let index = store.assets.findIndex((value: any) => value.id === data.id);
    if (index > -1) {
      store.setProp({ key: 'assets', index, value: data });
    }
    $toast?.success(`Asset actualizado con exito`);
  } catch (error) {
    $toast?.error('Ocurrió un error al intentar actualizar');
  }

  handleClose();
};

const handleDeleteRestoreAsset = async () => {
  if (selectedAsset?.value?.id) {
    let response = null;
    try {
      if (isViewActive.value) {
        response = await AssetsRepository.deleteAsset(selectedAsset.value.id);
      } else {
        response = await AssetsRepository.restoreAsset(selectedAsset.value.id);
      }

      if (response === 200) {
        store.deleteItem({
          key: 'assets',
          value: selectedAsset?.value?.id,
        });
      }
    } catch (error) {
      if (isViewActive.value) {
        return $toast?.error('Error al intentar eliminar');
      }
      $toast?.error('Error al intentar restaurar');
    }

    handleClose();
  }
};
</script>

<template>
  <Layout title="Assets" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>Activos</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>Inactivos</template>
          </CButton>
        </div>
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content> Agregar asset </template>
        </CButton>
      </div>

      <div class="form__logEmail">
        <Form
          ref="searchFormRef"
          @onSubmit="handleOnSubmit"
          :inputs="fieldsFilter"
          customClass="poliza__form"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
            >
              <template #content> Buscar </template>
            </CButton>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="handleReset"
            >
              <template #content> Limpiar filtro </template>
            </CButton>
          </template>
        </Form>
      </div>

      <DataTable
        :loading="isLoadingAssets"
        :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="assetsPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(assetInfo)="assetData">
          <div
            @click="handleSelectAsset(assetData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon restore"
          >
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateAsset'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputAsset"
            @onSubmit="handleCreateAsset"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>Agregar asset</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateAsset'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputAsset"
            @onSubmit="handleUpdateAsset"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>Actualizar</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `¿Está seguro que desea borrar el asset ${selectedAsset?.description}?`
                : `¿Está seguro que desea restaurar el asset ${selectedAsset?.description}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDeleteRestoreAsset"
            >
              <template #content>
                <div>
                  <span>Confirmar</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
